import React, { useState, useEffect, useRef } from 'react';
import { ModalWrapper, ButtonGroup, TableWrapper, OnoMailSendModal } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Row, Col, Popconfirm, Select, Table, TimePicker, Checkbox, Spin } from 'antd';
import { useProjectScheduleDetail, useRefCode, useProjectSchedule } from 'hooks';
import { DatePicker, LectureTime, Lecturer, LecturePlace, DailyOperator, LectureSubject, PmCm, Evaluation, NotionUrl, LectureTarget } from 'components/atoms'
import { getCodeNumberArray, getCodeArray } from 'utils/form-utils';
import moment, { Moment } from 'moment';
import CheckDuplicateModal from 'components/molecules/CheckDuplicateModal';
import { fetcher } from 'services';
import { usePopupManager } from 'react-popup-manager';
import { dateFormat } from 'utils/date-utils';
import { ColumnsType } from 'antd/lib/table/interface';
import { IMaskInput } from "react-imask";
import { Column } from 'react-virtualized';
import './index.scss';

export interface ProjectScheduleEditModalProps extends PopupProps {
    group_id: number;
    id: number;
    view_mode?: boolean;
}

const ProjectScheduleEditModal = ({ 
    isOpen, 
    onClose, 
    id, group_id, view_mode = false}: ProjectScheduleEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const [isCheckConfirm, setIsCheckConfirm] = useState<boolean>(false);   
    const [checkMessage, setCheckMessage] = useState<string>(''); 
    const [maxPrice, setMaxPrice] = useState<number>(0);
    const [scheduleSrl, setScheduleSrl] = useState<number>(id);
    const modalRef = useRef<null | HTMLDivElement>(null);
    const [startTime, setStartTime] = useState<string>('09:00');
    const [endTime, setEndTime] = useState<string>('09:00');   
    const [isOnoLecture, setIsOnoLecture]  = useState<boolean>(false);
    const [movieLinkUrl, setMovieLinkUrl] = useState<string>();
    const [satisfactionLinkUrl, setSatisfactionLinkUrl] = useState<string>();
    const [onoInfo, setOnoInfo] = useState<ProjectScheduleOnoInfoDto>();
    const [saveData, setSaveData] = useState<any>(); 

  /* ======================================================
  * hooks
  * ====================================================== */
  const { 
    isLoading,
    isLoadingInsert,
    isLoadingUpdate,
    isLoadingDelete,    
    scheduleInfo, 
    refetch: detailRefetch, 
    scheduleOnoInfo, 
    lastInsertSrl, 
    insertSuccess, 
    updateSuccess, 
    deleteSuccess, 
    projectScheduleInsert, 
    projectScheduleUpdate, 
    projectScheduleDelete } = useProjectScheduleDetail(scheduleSrl, group_id);
  const { scheduleList, refetch } = useProjectSchedule(group_id);
  const { refCodeList: lectureTypeList } = useRefCode({field:'lecture_type1'});   
  const [ lectureTypeCodeList, setlectureTypeCodeList] = useState<RefCodeListDto[] | any[]>();

   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { TextArea } = Input;
  const { open } = usePopupManager(); 

  const configBlocks = {
    PriceInput: {
      mask: Number,
      signed: true,  // disallow negative
      thousandsSeparator: ",",  // any single char
      padFractionalZeros: false,
      normalizeZeros: false,
      unmask: true // true|false|'typed'
    }
  }

  const onoColumns: ColumnsType<ProjectScheduleOnoInfoDto> = [
    {
        title: '웰컴메일',
        dataIndex: 'mail_type01',
        key: 'mail_type01',
        width: '100px',
        align: 'center',
        onCell: (record) => {
            return {
                onClick: (e) => {
                e.stopPropagation();
                onSendMail(record.schedule_srl, 'type01');
                },
            };
        },
        render: (text, record) => {
            const color = record.mail_type01_status==='40'?'#d9363e':record.mail_type01_status==='20'?'#52c41a':record.mail_type01_status==='30'?'#faad14':'black';
            return (
            <label style={{color:color}}>{text}</label>
            )
        }         
    },     
    {
        title: '일정안내',
        dataIndex: 'mail_type02',
        key: 'mail_type02',
        width: '100px',
        align: 'center',
        onCell: (record) => {
            return {
                onClick: (e) => {
                e.stopPropagation();
                onSendMail(record.schedule_srl, 'type02');
                },
            };
        },
        render: (text, record) => {
            const color = record.mail_type02_status==='40'?'#d9363e':record.mail_type02_status==='20'?'#52c41a':record.mail_type02_status==='30'?'#faad14':'black';
            return (
            <label style={{color:color}}>{text}</label>
            )
        }         
    }, 
    {
        title: '리플렉션',
        dataIndex: 'mail_type03',
        key: 'mail_type03',
        width: '100px',
        align: 'center',
        onCell: (record) => {
            return {
                onClick: (e) => {
                e.stopPropagation();
                onSendMail(record.schedule_srl, 'type03');
                },
            };
        },
        render: (text, record) => {
            const color = record.mail_type03_status==='40'?'#d9363e':record.mail_type03_status==='20'?'#52c41a':record.mail_type03_status==='30'?'#faad14':'black';
            return (
            <label style={{color:color}}>{text}</label>
            )
        }         
    }, 
    {
        title: '만족도 조사',
        dataIndex: 'mail_type04',
        key: 'mail_type04',
        width: '100px',
        align: 'center',
        onCell: (record) => {
            return {
                onClick: (e) => {
                e.stopPropagation();
                onSendMail(record.schedule_srl, 'type04');
                },
            };
        },
        render: (text, record) => {
            const color = record.mail_type04_status==='40'?'#d9363e':record.mail_type04_status==='20'?'#52c41a':record.mail_type04_status==='30'?'#faad14':'black';
            return (
            <label style={{color:color}}>{text}</label>
            )
        }         
    },         
  ]

  const columns: ColumnsType<ProjectScheduleInfoDto> = [
    {
      title: '강사',
      dataIndex: 'lecturer',
      key: 'lecturer',
      width: '100px',
      align: 'center',
      ellipsis: true,
    },
    {
     title: '강의일자',
     dataIndex: 'schedule_date',
     key: 'schedule_date',
     width: 'auto',
     align: 'center',
     ellipsis: true,
     render: (schedule_date, record) => {
        let scheduleTime = `${schedule_date} ${record.start_time} ~ ${record.end_time} (${record.time_count}시간)`;
        if (record.isExcptionPrice == 'Y') {
            scheduleTime = `${schedule_date} 예상매출`;
        }
        return (            
            (scheduleSrl === record.id)?(
                <span style={record.dup_count > 1 ? {color:'#f00'}:{}}>{scheduleTime}</span>
            ):(
                <a style={record.dup_count > 1 ? {color:'#f00'}:{}} onClick={()=>{
                    setScheduleSrl(record.id);         
                    ScrollToTop();
                }}>{scheduleTime}</a>
            )
            
        )
      }      
   },   
   {
    title: '강사매출',
    dataIndex: 'lecturer_price',
    key: 'lecturer_price',
    width: '100px',
    align: 'right',
    ellipsis: true,
    render: (lecturer_price, record) => {
      return (
        lecturer_price && lecturer_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      )
    }      
  }   
  ];  
   /* ======================================================
   * events
   * ====================================================== */
   function ScrollToTop() {    
    
    modalRef.current?.scrollIntoView(true);
  }

    const onSendMail = (id: number, mail_type: string) => {          
        if (!!onoInfo && onoInfo.email != '' ) {
            open(OnoMailSendModal, {
                id: id,
                mail_type: mail_type,
                onClose: (isOk) => {
                if (isOk) {
                    detailRefetch();
                };
                },
            });  
        }
    }     
    
    const onCancel = () => {
        onClose?.(insertSuccess || updateSuccess ||deleteSuccess);
    };

    const onCopy = () => {
       // console.log("onCopy");
        if (!!scheduleInfo) {
            const sData = {
                id: 0,
                project_group_srl: scheduleInfo.project_group_srl,
                notion_url: form.getFieldValue('notion_url'),
                lecture_place_type: form.getFieldValue('lecture_place_type'),
                lecture_studio: form.getFieldValue('lecture_studio'),
                location: form.getFieldValue('location'),
                lecture_target: form.getFieldValue('lecture_target').map((code) => code).join(',') || '',
                people_count: form.getFieldValue('people_count'),
                target_memo: form.getFieldValue('target_memo'),
                lecture_type1: form.getFieldValue('lecture_type'),             
                memo: form.getFieldValue('memo'),
                lecturer_srl: form.getFieldValue('lecturer_srl'),
                schedule_date: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD'),
                start_time: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + startTime,
                end_time: endTime < startTime? moment(form.getFieldValue('schedule_date')).add(1, 'days').format('YYYY-MM-DD')  + ' ' + endTime : dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + endTime, 
                time_count: form.getFieldValue('time_count'),
                operating_price: Number(form.getFieldValue('operating_price').toString().replaceAll(',','')) | 0, 
                lecturer_price: Number(form.getFieldValue('lecturer_price').toString().replaceAll(',','')) | 0, 
                diagnose_price: Number(form.getFieldValue('diagnose_price').toString().replaceAll(',','')) | 0, 
                consulting_price: Number(form.getFieldValue('consulting_price').toString().replaceAll(',','')) | 0, 
                plan_price: Number(form.getFieldValue('plan_price').toString().replaceAll(',','')) | 0, 
                studio_price: Number(form.getFieldValue('studio_price').toString().replaceAll(',','')) | 0, 
                traffic_price: Number(form.getFieldValue('traffic_price').toString().replaceAll(',','')) | 0, 
                teaching_price: Number(form.getFieldValue('teaching_price').toString().replaceAll(',','')) | 0, 
                etc_price: Number(form.getFieldValue('etc_price').toString().replaceAll(',','')) | 0,  
                outside_order_price: Number(form.getFieldValue('outside_order_price').toString().replaceAll(',','')) | 0, 
                outside_order_memo: form.getFieldValue('outside_order_memo'), 
                lecture_subject1: form.getFieldValue('lecture_subject1'),
                lecture_subject_memo: form.getFieldValue('lecture_subject_memo'),
                pm: form.getFieldValue('pm') ? form.getFieldValue('pm').map((code) => code).join(',') : '',
                cm: form.getFieldValue('cm') ? form.getFieldValue('cm').map((code) => code).join(',') : '',
                need_operater_yn: form.getFieldValue('need_operater_yn')?'Y':'N',
                need_operater_num: form.getFieldValue('need_operater_num'),
                class_evaluation: form.getFieldValue('class_evaluation'),
                class_evaluation_max: form.getFieldValue('class_evaluation_max'),
                nps_evaluation: form.getFieldValue('nps_evaluation'),
                is_no_evaluation: form.getFieldValue('is_no_evaluation') ? 'Y' : 'N',
                isExcptionPrice: form.getFieldValue('isExcptionPrice') ? 'Y' : 'N',
                onoinfo: isOnoLecture ? {
                    project_group_srl: scheduleInfo.project_group_srl,  
                    lecture_cha: form.getFieldValue('ono_lecture_cha'),
                    name_kor: form.getFieldValue('ono_name_kor'),
                    email: form.getFieldValue('ono_email'),
                    phone: form.getFieldValue('ono_phone'),
                    movie_link: form.getFieldValue('ono_movie_link'),
                    location_memo: form.getFieldValue('ono_location_memo'),
                    id: form.getFieldValue('ono_id'),
                    pwd: form.getFieldValue('ono_pwd'),
                    satisfaction_link: form.getFieldValue('ono_satisfaction_link'),
                }: null
            }

            setSaveData(sData);

            const preStartTime = scheduleInfo?.schedule_date + ' ' + scheduleInfo?.start_time;
            const preEndTime = scheduleInfo?.schedule_date + ' ' + scheduleInfo?.end_time;
            
            if ((sData.start_time && sData.end_time && (sData.lecturer_srl || (sData.lecture_place_type === 'ONLINE' && sData.lecture_studio && sData.lecture_studio !== 'ETC'))) &&
                (scheduleSrl == 0 || sData.start_time != preStartTime || sData.end_time != preEndTime || sData.lecturer_srl != scheduleInfo?.lecturer_srl || scheduleInfo?.lecture_place_type != sData.lecture_place_type || scheduleInfo?.lecture_studio != sData.lecture_studio)) {
                // 중복 검사            
                // 중복 검사
                fetcher.project.postProjectScheduleCheck({
                    id: 0,
                    lecture_studio: sData.lecture_studio,
                    lecturer_srl: sData.lecturer_srl,
                    start_time: sData.start_time,
                    end_time: sData.end_time,
                })
                .then((res) => {
                    switch (res.code) {
                        case 200:
                            projectScheduleInsert(sData)                             
                        break;
                        default:  
                            if (scheduleInfo.project_step == 'A' || res.message.startsWith('[확정]')) {
                                setCheckMessage(res.message);
                            } else {
                                setCheckMessage(res.message + '\n\n' + '대기로 입력하시겠습니까?');
                            }
                            setIsCheckConfirm(true);
                        break;
                    };
                });                 
            } else {
                projectScheduleInsert(sData)
            }


        }
    }

    const onCalcMaxPirce = () => {
        setMaxPrice(
            Number(form.getFieldValue('operating_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('lecturer_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('diagnose_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('consulting_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('plan_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('studio_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('traffic_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('teaching_price').toString().replaceAll(',','')) +
            Number(form.getFieldValue('etc_price').toString().replaceAll(',',''))
        )     
    }    

    const onSubmit = (e) => {

        if (!!scheduleInfo) {

            if (maxPrice < scheduleInfo.bill_price) {
                alert("총 매출이 기 발행된 매출 보다 작을 수 없습니다.");
                return;
            } 

            if (!!e.class_evaluation && (e.class_evaluation < 0 || e.class_evaluation_max < e.class_evaluation)) {
                alert("강의 평점 입력이 잘못되었습니다.");
                return;
            } 

            const sData = {
                id: scheduleSrl,
                project_group_srl: scheduleInfo.project_group_srl,
                notion_url: e.notion_url,
                lecture_place_type: e.lecture_place_type,
                lecture_studio: e.lecture_studio,
                location: e.location,
                lecture_target: e.lecture_target.map((code) => code).join(',') || '',
                people_count: e.people_count,
                target_memo: e.target_memo, 
                lecture_type1: e.lecture_type,               
                memo: e.memo,
                lecturer_srl: e.lecturer_srl,
                schedule_date: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD'),
                start_time: dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + startTime,
                end_time: endTime < startTime? moment(form.getFieldValue('schedule_date')).add(1, 'days').format('YYYY-MM-DD')  + ' ' + endTime : dateFormat(form.getFieldValue('schedule_date'), 'YYYY-MM-DD')  + ' ' + endTime, 
                time_count: e.time_count,
                operating_price: Number(form.getFieldValue('operating_price').toString().replaceAll(',','')),
                lecturer_price: Number(form.getFieldValue('lecturer_price').toString().replaceAll(',','')), 
                diagnose_price: Number(form.getFieldValue('diagnose_price').toString().replaceAll(',','')), 
                consulting_price: Number(form.getFieldValue('consulting_price').toString().replaceAll(',','')), 
                plan_price: Number(form.getFieldValue('plan_price').toString().replaceAll(',','')), 
                studio_price: Number(form.getFieldValue('studio_price').toString().replaceAll(',','')), 
                traffic_price: Number(form.getFieldValue('traffic_price').toString().replaceAll(',','')), 
                teaching_price: Number(form.getFieldValue('teaching_price').toString().replaceAll(',','')), 
                etc_price: Number(form.getFieldValue('etc_price').toString().replaceAll(',','')), 
                outside_order_price: Number(form.getFieldValue('outside_order_price').toString().replaceAll(',','')),
                outside_order_memo: e.outside_order_memo, 
                lecture_subject1: e.lecture_subject1,
                lecture_subject_memo: e.lecture_subject_memo,
                pm: e.pm ? e.pm.map((code) => code).join(',') : '',
                cm: e.cm ? e.cm.map((code) => code).join(',') : '',
                need_operater_yn: e.need_operater_yn?'Y':'N',
                need_operater_num: e.need_operater_num,
                class_evaluation: e.class_evaluation,
                class_evaluation_max: e.class_evaluation_max,
                nps_evaluation: e.nps_evaluation,
                is_no_evaluation: e.is_no_evaluation ? 'Y' : 'N',
                isExcptionPrice: e.isExcptionPrice ? 'Y' : 'N',
                onoinfo: isOnoLecture ? {
                    project_group_srl: scheduleInfo.project_group_srl,  
                    lecture_cha: form.getFieldValue('ono_lecture_cha'),
                    name_kor: form.getFieldValue('ono_name_kor'),
                    email: form.getFieldValue('ono_email'),
                    phone: form.getFieldValue('ono_phone'),
                    movie_link: form.getFieldValue('ono_movie_link'),
                    location_memo: form.getFieldValue('ono_location_memo'),
                    id: form.getFieldValue('ono_id'),
                    pwd: form.getFieldValue('ono_pwd'),
                    satisfaction_link: form.getFieldValue('ono_satisfaction_link'),
                }: null                
            }

            setSaveData(sData);

            const preStartTime = scheduleInfo?.schedule_date + ' ' + scheduleInfo?.start_time;
            const preEndTime = scheduleInfo?.schedule_date + ' ' + scheduleInfo?.end_time;
            
            if ((sData.start_time && sData.end_time && (sData.lecturer_srl || (sData.lecture_place_type === 'ONLINE' && sData.lecture_studio && sData.lecture_studio !== 'ETC'))) &&
                (scheduleSrl == 0 || sData.start_time != preStartTime || sData.end_time != preEndTime || sData.lecturer_srl != scheduleInfo?.lecturer_srl || scheduleInfo?.lecture_place_type != sData.lecture_place_type || scheduleInfo?.lecture_studio != sData.lecture_studio)) {
                // 중복 검사
                fetcher.project.postProjectScheduleCheck({
                    id: scheduleSrl,
                    lecture_studio: sData.lecture_studio,
                    lecturer_srl: sData.lecturer_srl,
                    start_time: sData.start_time,
                    end_time: sData.end_time,
                })
                .then((res) => {
                    switch (res.code) {
                        case 200:
                            if (scheduleSrl > 0) {
                                projectScheduleUpdate(sData)
                            } else {
                                projectScheduleInsert(sData)
                            }                                
                        break;
                        default: 
                            if (scheduleInfo.project_step == 'A' || res.message.startsWith('[확정]')) {
                                setCheckMessage(res.message);
                            } else {
                                setCheckMessage(res.message + '\n\n' + '대기로 입력하시겠습니까?');
                            }

                            setIsCheckConfirm(true);
                        break;
                    };
                });                 
            } else {
                if (scheduleSrl > 0) {
                    projectScheduleUpdate(sData)
                } else {
                    projectScheduleInsert(sData)
                }  
            }
        }
 
    };

    const onChangeLectureType = (val, opt) => {
        setIsOnoLecture(!!opt && opt.option == 'ono');        
    }

  /* ======================================================
   * effects
   * ====================================================== */
    useEffect(() => {
        setIsOnoLecture(false);
        setOnoInfo(undefined);
        if (!!scheduleInfo) {
            setMaxPrice(scheduleInfo.max_price);
            form.setFieldsValue({
                notion_url: scheduleInfo.notion_url || scheduleInfo.group_notion_url,
                lecture_place_type: scheduleInfo.lecture_place_type,
                lecture_studio: scheduleInfo.lecture_studio,
                location: scheduleInfo.location,
                memo: scheduleInfo.memo,
                lecture_target: scheduleInfo.lecture_target? getCodeArray(scheduleInfo.lecture_target) : getCodeArray(scheduleInfo.group_lecture_target),
                people_count: scheduleInfo.people_count === 0? scheduleInfo.group_people_count : scheduleInfo.people_count,
                target_memo: scheduleInfo.target_memo?scheduleInfo.target_memo:scheduleInfo.group_target_memo,
                lecturer_srl: scheduleInfo.lecturer_srl,
                schedule_date: (!!scheduleInfo.schedule_date && scheduleInfo.schedule_date !== '0000-00-00') ? moment(scheduleInfo.schedule_date, "YYYY-MM-DD") : undefined,
                lecture_time: scheduleInfo.start_time ? [moment(scheduleInfo.start_time, 'HH:mm'), moment(scheduleInfo.end_time, 'HH:mm')]: [] ,
                time_count: scheduleInfo.time_count,
                lecture_type: scheduleInfo.lecture_type1,
                lecture_subject1: scheduleInfo.lecture_subject1,
                lecture_subject_memo: scheduleInfo.lecture_subject_memo,
                pm: getCodeNumberArray(scheduleInfo.pm),
                cm: getCodeNumberArray(scheduleInfo.cm),
                need_operater_yn: scheduleInfo.need_operater_yn==='Y',
                need_operater_num: scheduleInfo.need_operater_num,                
                operating_price: scheduleInfo.operating_price || 0,
                lecturer_price: scheduleInfo.lecturer_price || 0,
                diagnose_price: scheduleInfo.diagnose_price || 0,
                consulting_price: scheduleInfo.consulting_price || 0,
                plan_price: scheduleInfo.plan_price || 0,
                studio_price: scheduleInfo.studio_price || 0,
                traffic_price: scheduleInfo.traffic_price || 0,
                teaching_price: scheduleInfo.teaching_price || 0,
                etc_price: scheduleInfo.etc_price || 0,        
                outside_order_price: scheduleInfo.outside_order_price || 0,                
                outside_order_memo: scheduleInfo.outside_order_memo,                
                class_evaluation: scheduleInfo.class_evaluation,
                class_evaluation_max: scheduleInfo.class_evaluation_max || 5,
                nps_evaluation: scheduleInfo.nps_evaluation,
                is_no_evaluation: scheduleInfo.is_no_evaluation === 'Y',
                isExcptionPrice: scheduleInfo.isExcptionPrice === 'Y',
                ono_lecture_cha: 1,
                ono_name_kor: '',
                ono_email: '',
                ono_phone: '',
                ono_movie_link: '',
                ono_location_memo: '',
                ono_id: '',
                ono_pwd: '',
                ono_satisfaction_link: '',
                
            });
            setStartTime(scheduleInfo.start_time?scheduleInfo.start_time:"09:00");
            setEndTime(scheduleInfo.end_time?scheduleInfo.end_time:"09:00");

            const idx = lectureTypeList?.findIndex((item) => scheduleInfo.lecture_type1 == item.name) || 0;
            if (idx >= 0) {
                setIsOnoLecture(!!lectureTypeList?.[idx].option && lectureTypeList?.[idx].option == 'ono') 
                
                //console.log("check option:", !!lectureTypeList?.[idx].option && lectureTypeList?.[idx].option == 'ono');
            }

            if (!!scheduleOnoInfo) {
                setOnoInfo(scheduleOnoInfo);
                form.setFieldsValue({                    
                    ono_lecture_cha: scheduleOnoInfo.lecture_cha && scheduleOnoInfo.lecture_cha > 0 ? scheduleOnoInfo.lecture_cha : 1,
                    ono_name_kor: scheduleOnoInfo.name_kor,
                    ono_email: scheduleOnoInfo.email,
                    ono_phone: scheduleOnoInfo.phone,
                    ono_movie_link: scheduleOnoInfo.movie_link,
                    ono_location_memo: scheduleOnoInfo.location_memo,
                    ono_id: scheduleOnoInfo.id,
                    ono_pwd: scheduleOnoInfo.pwd,
                    ono_satisfaction_link: scheduleOnoInfo.satisfaction_link,
                });                
            } else {
                setOnoInfo({
                    schedule_srl: scheduleInfo.id,
                    project_group_srl: scheduleInfo.project_group_srl,  
                    mail_type01: '-',
                    mail_type02: '-',
                    mail_type03: '-',  
                    mail_type04: '-', 
                    mail_type01_status: '',
                    mail_type02_status: '',
                    mail_type03_status: '',
                    mail_type04_status: '',
                })
            }            
        }



        const options = lectureTypeList?.filter((item) => item.status_type === 'AV' || (scheduleInfo && scheduleInfo.lecture_type1 == item.name)) || [];
        setlectureTypeCodeList(options);

    }, [scheduleInfo, scheduleOnoInfo, lectureTypeList])

    useEffect(() => {
        if (insertSuccess || updateSuccess ||deleteSuccess) {
            refetch();
        }

        if (!!insertSuccess && lastInsertSrl > 0) setScheduleSrl(lastInsertSrl);

    }, [insertSuccess, updateSuccess, deleteSuccess])
    
    return (
        <>
            <ModalWrapper open={isOpen} onCancel={onCancel} width={750} title={scheduleSrl===0?'프로젝트 스케줄 등록':'프로젝트 스케줄 수정'} closable >
                <div style={{width:"100%"}} ref={modalRef}>
                <Spin spinning={isLoading || isLoadingInsert || isLoadingUpdate || isLoadingDelete} tip="처리중입니다. 잠시만 기다려 주세요." size="large">
                <Form
                    form={form}
                    name="ProjectInfo"
                    layout="horizontal"  
                    labelCol={{ flex: '120px' }}
                    onFinish={onSubmit}
                    style={{width:'100%'}}
                    autoComplete="off"
                >
                    <Row>
                        <Col span={9}>
                        <Form.Item label={'고객사(기업명)'} className='equal-height-item'>
                            <label>{scheduleInfo?.company_name}</label>                  
                        </Form.Item> 
                        </Col> 
                        <Col span={15}> 
                        <Form.Item label={'교육담당자'}>
                            <label>{scheduleInfo?.crm_name} / {scheduleInfo?.crm_email} / {scheduleInfo?.crm_phone} / {scheduleInfo?.crm_position}</label>                  
                        </Form.Item>   
                        </Col>            
                    </Row>
                    <Form.Item  label={'프로젝트명'}>
                        <label>{scheduleInfo?.group_name}</label>                   
                    </Form.Item>  
                    <Form.Item label={'강의요청서'}>                    
                        <NotionUrl form={form} url={(scheduleSrl > 0 && !!scheduleInfo?.notion_url ? scheduleInfo?.notion_url : scheduleInfo?.group_notion_url) + ''} />   
                    </Form.Item> 
                    <Row>
                        <Col span={9}>
                        <Form.Item name="lecturer_srl" label={'강사'} rules={[{ required: true, message: '' }]}>
                            <Lecturer form={form} required={true}/>
                        </Form.Item>                      
                        </Col> 
                        <Col span={15}> 
                            <Form.Item name="lecture_type" label={'강의 구분'} rules={[{ required: true, message: '' }]}>
                                <Select 
                                    placeholder="강의구분 선택"
                                    onSelect={(value, option)=>onChangeLectureType(value, option)}
                                >
                                {lectureTypeCodeList?.map((item) => {
                                    return (<Select.Option key={item.name} value={item.name} option={item.option}>{item.value}</Select.Option>)
                                })}                        
                                </Select>                                        
                            </Form.Item>  
                        </Col>                        
                    </Row> 
                    {isOnoLecture ? (
                    <>
                    <Row>
                        <Col span={19}>
                            <Form.Item label={'강의 주제'}>
                                <LectureSubject form={form} />
                            </Form.Item>                                                                                                   
                        </Col>
                        <Col span={5}> 
                            <Form.Item name="ono_lecture_cha" label={'회차'} labelCol={{ span: 10 }}>
                                <Select 
                                    defaultValue={1}
                                    options={Array.from({ length: 20 }, (_, i) => ({
                                        value: i + 1,
                                        label: `${i + 1}`,
                                    }))}
                                />
                            </Form.Item>  
                        </Col>                         
                    </Row>
                    <Row>
                        <Col span={9}> 
                            <Form.Item name="ono_name_kor" label={'대상자'}>
                                <Input type="text" placeholder=''/>     
                            </Form.Item>  
                        </Col> 
                        <Col span={8}> 
                            <Form.Item name="ono_phone" label={'연락처'}>
                                <Input type="text" placeholder=''/>     
                            </Form.Item>  
                        </Col>
                        <Col span={7}> 
                            <Form.Item name="ono_email" label={'이메일'} labelCol={{ span: 10 }}>
                                <Input type="text" placeholder=''/>     
                            </Form.Item>  
                        </Col>                                                 
                    </Row>
                    </>
                    ): (
                        <Form.Item label={'강의 주제'}>
                            <LectureSubject form={form} />
                        </Form.Item>  
                    )}

                    <Row>
                        <Col span={9}>
                        <Form.Item 
                            name="schedule_date" 
                            label={'교육 일자'}
                            rules={[{ required: true, message: '' }]}
                        >
                            <DatePicker showTime={false} format="YYYY-MM-DD" />                    
                        </Form.Item> 
                        </Col> 
                        <Col span={15}> 
                        <Form.Item label={'교육 대상'}>
                            <LectureTarget form={form} />    
                        </Form.Item>   
                        </Col>            
                    </Row>   
                    <Form.Item name="lecture_time" label={'강의 시간'}>
                        <Form.Item style={{ display: 'inline-block', marginRight: '5px'}}>
                        <TimePicker
                            format={'HH:mm'} 
                            minuteStep={5} 
                            showNow={false}
                            value={moment(startTime, "HH:mm")}
                            popupClassName={"noFooterTimePick"}
                            onSelect={(value) => {
                                const timeString = moment(value).format("HH:mm");
                                setStartTime(timeString);
                            }}
                        />  
                        <label>&nbsp;~&nbsp;</label>
                        <TimePicker
                            format={'HH:mm'} 
                            minuteStep={5} 
                            showNow={false}
                            value={moment(endTime, "HH:mm")}
                            popupClassName={"noFooterTimePick"}
                            onSelect={(value) => {
                                const timeString = moment(value).format("HH:mm");
                                setEndTime(timeString);
                            }}
                        />   
                        </Form.Item>
                        <Form.Item name="time_count" style={{ width:'50px', display: 'inline-block', marginRight: '5px' }}>
                            <Input type="number" placeholder='시간'/>  
                        </Form.Item>   
                        <Form.Item style={{ display: 'inline-block'}}>
                        <label>시간</label>                                           
                        </Form.Item>
                    </Form.Item>                                     
                    <Form.Item name="lecture_place_type" label={'교육 장소'} rules={[{ required: true, message: '' }]}>
                        <LecturePlace form={form} required={true} lecture_place_type={scheduleInfo?.lecture_place_type || 'OFFLINE'} lecture_studio={scheduleInfo?.lecture_studio || ''} showLocation={isOnoLecture} />
                    </Form.Item> 
                    {isOnoLecture && (   
                    <>
                    <Form.Item name="ono_location_memo"  label={'장소 메모'}>
                        <Input type="text" placeholder='장소 메모를 입력하세요' onChange={(e) => setSatisfactionLinkUrl(e.target.value)}/>                    
                    </Form.Item>                      
                    <Form.Item label={'영상학습'}>
                        <Form.Item name="ono_movie_link" style={{ width:'calc(100% - 60px - 10px)', display: 'inline-block', marginRight: '5px' }}>
                            <Input type="text" placeholder='링크가 있을경우 입력하세요' onChange={(e) => setMovieLinkUrl(e.target.value)}/>                    
                        </Form.Item>   
                        <Button style={{ width:'60px', display: 'inline-block', marginRight: '5px' }} onClick={()=>window.open(movieLinkUrl, "movie", "noopener, noreferrer")} disabled={!movieLinkUrl}>보기</Button>  
                    </Form.Item>  
                    <Row>
                        <Col span={12}>
                        <Form.Item name="ono_id" label={'ID'}>
                            <Input type="text" />                   
                        </Form.Item> 
                        </Col> 
                        <Col span={12}> 
                        <Form.Item name="ono_pwd" label={'PW'}>
                            <Input type="text" />                   
                        </Form.Item>   
                        </Col>
                    </Row> 
                    <Form.Item label={'만족도조사'}>
                        <Form.Item name="ono_satisfaction_link" style={{ width:'calc(100% - 60px - 10px)', display: 'inline-block', marginRight: '5px' }}>
                            <Input type="text" placeholder='링크가 있을경우 입력하세요' onChange={(e) => setSatisfactionLinkUrl(e.target.value)}/>                    
                        </Form.Item>   
                        <Button style={{ width:'60px', display: 'inline-block', marginRight: '5px' }} onClick={()=>window.open(satisfactionLinkUrl, "movie", "noopener, noreferrer")} disabled={!satisfactionLinkUrl}>보기</Button>  
                    </Form.Item>
                    <Form.Item label={'메일 발송 현황'}>
                        <Table
                            tableLayout="fixed"
                            dataSource={onoInfo?[onoInfo]:[]}
                            pagination={false}
                            columns={onoColumns}
                            style={{ width: '460px', overflowX: 'hidden', overflowY: 'hidden', height:70}}
                            showSorterTooltip={false}
                        />                        
                    </Form.Item>                                                           
                    </>                 
                    )}
                    <Form.Item className='form_pmcm' label={'PM/CM/운영'}>
                        <PmCm form={form} pm={scheduleInfo?.pm} cm={scheduleInfo?.cm}/>
                        <div>
                            <Form.Item style={{ display: 'inline-block'}}>
                            <label>운영 :&nbsp;</label>
                            </Form.Item>         
                            <Form.Item style={{ display: 'inline-block'}}>
                            <DailyOperator form={form} project_srl={group_id} operator={scheduleInfo?.operator} schedule_date={scheduleInfo?.schedule_date} />
                            </Form.Item>  
                        </div>                         
                    </Form.Item>                     
                    <Form.Item label={'총매출'}>
                        <Form.Item style={{ display: 'inline-block'}}>
                            <label>{maxPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</label>
                        </Form.Item>
                        <Form.Item name="isExcptionPrice" valuePropName="checked" style={{ display: 'inline-block', marginLeft: '20px' }}>
                            <Checkbox>예상매출 여부</Checkbox>                
                        </Form.Item>                         
                    </Form.Item>   
                    <Form.Item name='lecturer_price' label={'강사 매출'}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('lecturer_price', value);onCalcMaxPirce();})}/>                
                    </Form.Item>   
                    <Row>
                        <Col span={12}> 
                            <Form.Item name='teaching_price' label={'교재비 매출'}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('teaching_price', value);onCalcMaxPirce();})}/>                     
                            </Form.Item>   
                        </Col>                                
                        <Col span={12}>
                            <Form.Item name='traffic_price' label={'교통비 매출'}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('traffic_price', value);onCalcMaxPirce();})}/>                  
                            </Form.Item> 
                        </Col>                     
                    </Row>  
                    <Row>
                        <Col span={12}> 
                            <Form.Item name='plan_price' label={'설계비 매출'}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('plan_price', value);onCalcMaxPirce();})}/>                   
                            </Form.Item>   
                        </Col>            
                        <Col span={12}>
                            <Form.Item name='operating_price' label={'운영비 매출'}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('operating_price', value);onCalcMaxPirce();})}/>                 
                            </Form.Item> 
                        </Col> 
                    </Row>   
                    <Row>
                        <Col span={12}> 
                            <Form.Item name='diagnose_price' label={'HSG진단 매출'}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('diagnose_price', value);onCalcMaxPirce();})}/>                    
                            </Form.Item>  
                        </Col>            
                        <Col span={12}>
                            <Form.Item name='consulting_price' label={'HSG컨설팅 매출'}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('consulting_price', value);onCalcMaxPirce();})}/>                 
                            </Form.Item> 
                        </Col> 
                    </Row>                  
                    <Row>
                        <Col span={12}>
                            <Form.Item name='studio_price' label={'스튜디오비 매출'}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('studio_price', value);onCalcMaxPirce();})}/>                
                            </Form.Item> 
                        </Col>                     
                        <Col span={12}> 
                            <Form.Item name='etc_price' label={'기타비 매출'}>
                            <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('etc_price', value);onCalcMaxPirce();})}/>                    
                            </Form.Item> 
                        </Col>            
                    </Row>                              
                    <Form.Item name='memo' label={'메모'}>
                        <TextArea rows={3} placeholder='메모를 입력하세요'/>   
                    </Form.Item>                        
                    <Form.Item label={'외주비'}>
                        <Form.Item name='outside_order_price' style={{ width:'150px', display: 'inline-block', marginRight: '5px'}}>
                        <IMaskInput blocks = {configBlocks} mask = "PriceInput" className={'ant-input'} unmask={'typed'} onAccept = {((value, mask) => {form.setFieldValue('outside_order_price', value);})}/>                    
                        </Form.Item>                          
                        <Form.Item name='outside_order_memo' style={{ width:'calc(100% - 150px - 5px)', display: 'inline-block'}}>
                            <Input type="text"  placeholder='외주비 메모를 입력하세요'/>
                        </Form.Item>                                        
                    </Form.Item>    
                    <Form.Item label={'강의평점/NPS'}>
                        <Evaluation form={form} />
                    </Form.Item>   
     
                    {(!view_mode) && (
                        <ButtonGroup className={'buttonsWrapper'}>
                            <div>
                                <Button onClick={onCancel} style={{marginRight: "5px"}}>닫기</Button>
                                {(scheduleSrl>0) && (
                                <Popconfirm
                                    title="시간 수정 하였습니까?"
                                    okText="복사"
                                    cancelText="취소"
                                    onConfirm={() => { onCopy()}}
                                >   
                                    <Button>복사</Button>
                                </Popconfirm>                       
                                )}
                            </div>   
                            <div>
                                {(scheduleSrl>0 && !!scheduleInfo && scheduleInfo.bill_price === 0 && scheduleInfo.out_lecturer_price_yn === 'N') && (
                                    <Popconfirm
                                        title="삭제 하시겠습니까?"
                                        okText="삭제"
                                        cancelText="취소"
                                        onConfirm={() => { projectScheduleDelete({id:scheduleSrl})}}
                                    >   
                                        <Button danger style={{marginRight: "5px"}}>삭제</Button>        
                                    </Popconfirm>
                                )}
                                <Button htmlType="submit"  type="primary">{scheduleSrl===0?'등록':'수정'}</Button>
                            </div>                    
                        </ButtonGroup>    
                    )}  
                </Form>
                <TableWrapper isSearch={false}>
                    <Table
                        tableLayout="fixed"
                        dataSource={scheduleList}
                        pagination={false}
                        columns={columns}
                        style={{ width: '100%', overflowX: 'auto',marginTop:'15px'}}
                        showSorterTooltip={false}
                        scroll={{ y: 240 }}
                        rowClassName={(record) => {
                            if (scheduleSrl === record.id) return 'bg-selected-row';
                            return '';
                        }}                        
                    />
                </TableWrapper>  
                </Spin>              
                </div>
            </ModalWrapper>  
            <CheckDuplicateModal
                title="스케줄 확인"
                message={checkMessage}
                visible={isCheckConfirm}
                onOk={() => {
                    setIsCheckConfirm(false)

                    if (scheduleInfo?.project_step != 'A' && !checkMessage.startsWith('[확정]')) {
                        if (scheduleSrl > 0) {
                            projectScheduleUpdate(saveData)
                        } else {
                            projectScheduleInsert(saveData)
                        } 
                    }
                }}
                onCancel={
                    (scheduleInfo?.project_step == 'A' || checkMessage.startsWith('[확정]')) ? undefined : ()=>setIsCheckConfirm(false)
                }                
            />          
        </>
    )    
};

export default React.memo(ProjectScheduleEditModal);    